<section class="min-h-screen">
  <!-- Header -->
  <div class="mb-6 flex items-start justify-between">
    <div>
      <h1 class="text-2xl font-bold tracking-tight text-primary-800 dark:text-primary-400">Institutions</h1>
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">Manage registered financial institutions and their settings</p>
    </div>
  </div>

  <!-- Actions Row -->
  <div class="flex flex-wrap items-center justify-between gap-3 mb-5">
    <div class="flex-1 min-w-[260px] max-w-xs">
      <form [formGroup]="searchQuery">
        <input type="text" formControlName="search" placeholder="Filter institutions..."
          class="w-full h-10 px-3 text-xs bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 focus:border-primary-800 transition-colors" />
      </form>
    </div>
    <div class="flex items-center gap-2">
      <app-institution-filter (onSaveEvent)="filterInstitutionByDate($event)"
        (resetClickEvent)="resetFilters()"></app-institution-filter>
      <app-export-button (onExportEvent)="handleExport($event)"></app-export-button>
      <button (click)="getPaginatedInstitutions()"
        class="px-3 py-1.5 rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 transition-colors flex items-center gap-1.5 text-xs font-semibold" title="Refresh">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>Refresh</span>
      </button>
      <div class="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <button type="button" (click)="viewMode = 'table'" [class]="viewMode === 'table' ? 'p-1.5 bg-primary-800 text-white' : 'p-1.5 bg-white dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:text-slate-400'" title="Table view">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
        </button>
        <button type="button" (click)="viewMode = 'grid'" [class]="viewMode === 'grid' ? 'p-1.5 bg-primary-800 text-white' : 'p-1.5 bg-white dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:text-slate-400'" title="Grid view">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"/></svg>
        </button>
      </div>
      @if (permissionService.canManage('institutions')) {
      <button (click)="onAddInstitution()"
        class="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-semibold transition-colors">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4" />
        </svg>
        Add Institution
      </button>
      }
    </div>
  </div>

  <!-- Table / Grid Toggle -->
  @if (viewMode === 'table') {
  <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden">
   <div class="overflow-x-auto">
    <div class="min-w-[1050px]">
    <!-- Table Header -->
    <div class="grid grid-cols-[75px_2fr_1.5fr_1fr_1.5fr_1.3fr_90px_70px] gap-2 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700 px-5 py-2.5">
      <div class="text-left"><p class="text-xs font-semibold text-slate-400">Entry Date</p></div>
      <div class="text-left"><p class="text-xs font-semibold text-slate-400">Entity Name</p></div>
      <div class="text-left"><p class="text-xs font-semibold text-slate-400">Global Address</p></div>
      <div class="text-left"><p class="text-xs font-semibold text-slate-400">Phone / Line</p></div>
      <div class="text-left"><p class="text-xs font-semibold text-slate-400">Email Contact</p></div>
      <div class="text-left"><p class="text-xs font-semibold text-slate-400">Assigned To</p></div>
      <div class="text-left"><p class="text-xs font-semibold text-slate-400">Current Status</p></div>
      <div class="text-center"><p class="text-xs font-semibold text-slate-400">Actions</p></div>
    </div>
    <div class="min-h-[240px]">
      @if (isInstitutionLoading) {
      <div class="flex items-center justify-center py-16"><app-progress-loader></app-progress-loader></div>
      } @else if (institutions.length) {
      @for (institution of institutions; track institution.id) {
      <app-institution-tabel [institution]="institution" (loadInstitutionEvent)="getPaginatedInstitutions()"
        (updateInstitutionEvent)="onUpdateIntitution($event)" (showOverlayEvent)="onShowOverlay()"
        [isUpdateInstitution]="isUpdateInstitution" (addMembersEvent)="addMember()"></app-institution-tabel>
      }
      } @else {
      <div class="flex flex-col items-center justify-center py-16 text-center">
        <p class="text-sm font-bold text-slate-800 dark:text-slate-100">No institutions registered</p>
        <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Add the first institution to start onboarding members.</p>
      </div>
      }
    </div>
    </div>
   </div>
  </div>
  } @else {
  <!-- Grid View -->
  <div class="min-h-[240px]">
    @if (isInstitutionLoading) {
    <div class="flex items-center justify-center py-16"><app-progress-loader></app-progress-loader></div>
    } @else if (institutions.length) {
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      @for (institution of institutions; track institution.id) {
      <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:shadow-md transition-shadow">
        <div class="flex items-start justify-between mb-3">
          <div class="min-w-0">
            <p class="text-sm font-bold text-slate-900 dark:text-white truncate">{{ institution.name || '—' }}</p>
            <span class="inline-block text-xs font-semibold text-slate-400 bg-slate-100 dark:bg-slate-900 px-1.5 py-0.5 rounded mt-0.5">{{ institution.code || 'NO-CODE' }}</span>
          </div>
          <span class="ml-2 shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-widest"
            [class]="institution.isActive ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900' : 'bg-slate-50 dark:bg-slate-800/60 text-slate-400 border-slate-200 dark:border-slate-700'">
            {{ institution.isActive ? 'Active' : 'Inactive' }}
          </span>
        </div>
        <div class="space-y-1.5 text-xs text-slate-500 dark:text-slate-400 mb-4">
          @if (institution.address) {
          <p class="truncate"><span class="font-semibold text-slate-400">Address:</span> {{ institution.address }}</p>
          }
          @if (institution.contact) {
          <p><span class="font-semibold text-slate-400">Phone:</span> {{ institution.contact }}</p>
          }
          @if (institution.email) {
          <p class="truncate"><span class="font-semibold text-slate-400">Email:</span> {{ institution.email }}</p>
          }
          @if (institution.assignedTo; as assignedTo) {
          <p class="truncate"><span class="font-semibold text-slate-400">Assigned To:</span> {{ assignedTo.firstName }} {{ assignedTo.lastName }}</p>
          }
        </div>
        <div class="pt-3 border-t border-slate-100 dark:border-slate-700/50 flex items-center justify-between">
          <p class="text-[11px] text-slate-400">{{ institution.createdAt | date:'dd MMM yyyy' }}</p>
          @if (permissionService.canManage('institutions')) {
          <button (click)="onUpdateIntitution(institution)" class="p-1.5 rounded text-slate-400 hover:text-primary-800 dark:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-900 transition-colors" title="Edit">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
          </button>
          }
        </div>
      </div>
      }
    </div>
    } @else {
    <div class="flex flex-col items-center justify-center py-16 text-center">
      <p class="text-sm font-bold text-slate-800 dark:text-slate-100">No institutions registered</p>
    </div>
    }
  </div>
  }

  <!-- Pagination -->
  @if (!isInstitutionLoading && institutions.length > 0) {
  <app-pagination [totalItems]="allInstitutions.meta.total || 0" [itemsPerPage]="queryParams.perPage || 10"
    [currentPage]="queryParams.page || 1" (pageChange)="onPageChange($event)"></app-pagination>
  }
</section>

<!-- Add Institution Modal -->
@if (isAddInstitution) {
<div
  class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300">
  <div class="absolute inset-0" (click)="onCloseInstitution()"></div>
  <div
    class="relative w-full max-w-4xl bg-white dark:bg-slate-800 rounded-2xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14),0_0_0_1px_rgba(0,0,0,0.02)] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
    <!-- Top Brand Glow -->
    <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/40 via-primary to-primary/40"></div>

    <div class="flex items-center justify-between px-10 py-6 border-b border-slate-100 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/60 backdrop-blur-md">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-2 h-2 rounded-full bg-primary-800 animate-pulse"></div>
          <h3 class="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-widest">Registry Hub</h3>
        </div>
        <p class="text-xxxs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">Register New Financial Entity</p>
      </div>
      <button (click)="onCloseInstitution()"
        class="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-900 dark:text-white hover:bg-white dark:bg-slate-800 hover:shadow-lg rounded-full transition-all duration-300 group">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
          class="group-hover:rotate-90 transition-transform duration-300">
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </button>
    </div>
    <div class="p-10 overflow-y-auto max-h-[80vh] custom-scrollbar">
      <app-add-institution (onCloseEvent)="onCloseInstitution()" (loadInstitutionEvent)="getPaginatedInstitutions()"
        [showAddCSV]="true" [institution]="institution"></app-add-institution>
    </div>
  </div>
</div>
}

<!-- Update Institution Modal -->
@if (isUpdateInstitution) {
<div
  class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300">
  <div class="absolute inset-0" (click)="onCloseUpdateInstitution()"></div>
  <div
    class="relative w-full max-w-4xl bg-white dark:bg-slate-800 rounded-2xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14),0_0_0_1px_rgba(0,0,0,0.02)] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
    <!-- Top Brand Glow -->
    <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/40 via-primary to-primary/40"></div>

    <div class="flex items-center justify-between px-10 py-6 border-b border-slate-100 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/60 backdrop-blur-md">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-2 h-2 rounded-full bg-primary-800"></div>
          <h3 class="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-widest">Profile Editor</h3>
        </div>
        <p class="text-xxxs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">Modify Administrative Entity</p>
      </div>
      <button (click)="onCloseUpdateInstitution()"
        class="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-900 dark:text-white hover:bg-white dark:bg-slate-800 hover:shadow-lg rounded-full transition-all duration-300 group">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
          class="group-hover:rotate-90 transition-transform duration-300">
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </button>
    </div>
    <div class="p-10 overflow-y-auto max-h-[80vh] custom-scrollbar">
      <app-add-institution (onCloseEvent)="onCloseUpdateInstitution()"
        (loadInstitutionEvent)="getPaginatedInstitutions()" [showAddCSV]="false"
        [institution]="institution"></app-add-institution>
    </div>
  </div>
</div>
}

<!-- Add Members Modal -->
@if (isAddMembers) {
<div
  class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300">
  <div class="absolute inset-0" (click)="onCloseAddMember()"></div>
  <div
    class="relative w-full max-w-2xl bg-white dark:bg-slate-800 rounded-2xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14),0_0_0_1px_rgba(0,0,0,0.02)] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
    <!-- Top Brand Glow -->
    <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/40 via-primary to-primary/40"></div>

    <div class="flex items-center justify-between px-10 py-6 border-b border-slate-100 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/60 backdrop-blur-md">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-2 h-2 rounded-full bg-primary-800 animate-pulse"></div>
          <h3 class="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-widest">Portfolio Ingestion</h3>
        </div>
        <p class="text-xxxs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">Batch Onboard Members</p>
      </div>
      <button (click)="onCloseAddMember()"
        class="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-900 dark:text-white hover:bg-white dark:bg-slate-800 hover:shadow-lg rounded-full transition-all duration-300 group">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
          class="group-hover:rotate-90 transition-transform duration-300">
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </button>
    </div>
    <div class="p-10">
      <app-add-members [institution]="institution" (onCloseEvent)="onCloseAddMember()"></app-add-members>
    </div>
  </div>
</div>
}